import { useEffect } from "react";

import { OutcomeChipList } from "./OutcomeChipList";

import { OUTCOME_ID_QUERY_PARAM, TAB_QUERY_PARAM } from "components/constants";
import { OutcomesNavSidebar } from "components/materials/outcomes/OutcomesNavSidebar";
import { FakeStudentOtherCourseOutcomeUseIndex } from "components/outcomes/FakeStudentOtherCourseOutcomeUseIndex";
import { OutcomeInfo } from "components/outcomes/OutcomeInfo";
import { OutcomeUseIndex } from "components/outcomes/OutcomeUseIndex";
import { StudentOtherCourseOutcomeUseIndex } from "components/outcomes/StudentOtherCourseOutcomeUseIndex";
import { StudentOutcomeUseIndex } from "components/outcomes/StudentOutcomeUseIndex";
import { t } from "i18n/i18n";
import { LegacyButton } from "mds/components/LegacyButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  selectCourseOutcomeRubric,
  selectCurrentCourseId,
  selectFullCourseOutcomes,
} from "store/selectors";
import { useFindItemBySearchParamId } from "utils/hooks/use-find-item-by-search-param-id";
import { courseHomeUrl, courseOutcomesUrl, withQueryParams } from "utils/urls";
import { isStudentDemoEnabled } from "utils/waffle-flags";

export const OutcomesView = () => {
  const isSmallScreen = !useIsMdOrLarger();
  const { canAuthorCourse, canParticipateInCourse } = useCapabilities();

  const courseId = useAppSelector(selectCurrentCourseId);
  const courseOutcomes = useAppSelector((s) => selectFullCourseOutcomes(s, courseId));
  const outcomes = courseOutcomes.map((courseOutcome) => courseOutcome.outcome);

  const currentOutcome = useFindItemBySearchParamId(outcomes, OUTCOME_ID_QUERY_PARAM);
  const outcomeId = currentOutcome?.id;

  const currentCourseOutcome = courseOutcomes.find(
    (courseOutcome) => courseOutcome.outcome_id === outcomeId,
  );

  const rubric = useAppSelector((s) => selectCourseOutcomeRubric(s, currentCourseOutcome?.id));

  useEffect(() => {
    storeApi.outcomes.list();
    storeApi.course_outcomes.list({ course_id: courseId });

    storeApi.orgs.list();
  }, [courseId]);

  return (
    <div className="flex h-full w-full flex-col gap-4 overflow-y-auto">
      {isSmallScreen && outcomes.length > 0 && (
        <div className="mt-4 w-full">
          <OutcomeChipList
            getOutcomeUrl={(outcome) => courseOutcomesUrl(courseId, outcome.id)}
            outcomes={outcomes}
          />
          {currentCourseOutcome && (
            <OutcomeInfo
              outcomeItem={currentCourseOutcome}
              rubric={rubric}
              isCourseLevel
              readOnly
            />
          )}
          {canAuthorCourse && (
            <div className="space-y-2">
              <LegacyButton
                kind="secondary"
                to={withQueryParams(courseHomeUrl(courseId), {
                  [TAB_QUERY_PARAM]: "assessments",
                })}
                mobileFullWidth
              >
                {t("course.view_assessments")}
              </LegacyButton>

              <LegacyButton
                kind="secondary"
                to={withQueryParams(courseHomeUrl(courseId), {
                  [TAB_QUERY_PARAM]: "engagement",
                })}
                mobileFullWidth
              >
                {t("course.view_engagement")}
              </LegacyButton>
            </div>
          )}
        </div>
      )}

      {!isSmallScreen && (
        <div className="flex">
          <OutcomesNavSidebar courseId={courseId} selectedOutcome={currentOutcome?.title} />

          <main className="h-full w-full">
            <OutcomeInfo
              outcomeItem={currentCourseOutcome}
              rubric={rubric}
              isCourseLevel
              // For now we only allow editing the description and title for outcomes at the org level page, even though
              // the instructor can edit the rubric at the course level.
              readOnly
            />
          </main>

          {outcomeId && !currentOutcome && <div className="page">{t("error.404_message")}</div>}
        </div>
      )}

      {currentCourseOutcome && canAuthorCourse && (
        <OutcomeUseIndex courseOutcome={currentCourseOutcome} />
      )}

      {currentCourseOutcome && canParticipateInCourse && (
        <StudentOutcomeUseIndex courseOutcome={currentCourseOutcome} />
      )}

      {currentCourseOutcome && canParticipateInCourse && !isStudentDemoEnabled() && (
        <StudentOtherCourseOutcomeUseIndex courseOutcome={currentCourseOutcome} />
      )}

      {isStudentDemoEnabled() && canParticipateInCourse && (
        <FakeStudentOtherCourseOutcomeUseIndex courseOutcome={currentCourseOutcome} />
      )}
    </div>
  );
};
